import { useMemo } from 'react'
import type { JSX } from 'react'

import { useDelayedLoading } from '@buffer-mono/popcorn'
import { startOfWeek, subDays, subWeeks } from 'date-fns'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { useAllChannelsPostInsights } from '~publish/pages/Insights/AllChannelsInsights/hooks/useAllChannelsPostInsights'
import type { AllChannelsPostInsightsResult } from '~publish/pages/Insights/AllChannelsInsights/hooks/useAllChannelsPostInsights'

import { WeeklyPulse } from './WeeklyPulse'

type SummaryEntry =
  AllChannelsPostInsightsResult['allChannelsPostInsights']['summary'][number]

const COMPARISON_LABEL = 'vs last week'

// The all-channels summary always returns [postCount, followers, reactions,
// comments]. Weekly Pulse surfaces three of those, in this order.
const WEEKLY_PULSE_METRIC_TYPES = ['postCount', 'followers', 'comments']

// The shared metric definition describes followers as the total "for the
// selected channels", which is accurate on the All Channels Insights page and
// its channel filter. Home has no channel selection and always shows the
// organization-wide total, so it needs its own wording.
const HOME_FOLLOWERS_DESCRIPTION = 'Total followers across all your channels'

export function InsightsSummaryModule(): JSX.Element {
  const organizationId = useOrganizationId()
  const weekStartsOn: 0 | 1 = useWeekStartsOn() === 'monday' ? 1 : 0

  // Current week so far, matching the scorecard's window on the same page.
  // The comparison must be explicit: with a partial week as the primary
  // window, the backend default compares against the equal-length period
  // immediately before it, which doesn't match the "vs last week" label.
  // Compare against the full previous week instead.
  const { startDate, endDate, comparisonStartDate, comparisonEndDate } =
    useMemo(() => {
      const now = new Date()
      const weekStart = startOfWeek(now, { weekStartsOn })
      return {
        startDate: weekStart,
        endDate: now,
        comparisonStartDate: subWeeks(weekStart, 1),
        comparisonEndDate: subDays(weekStart, 1),
      }
    }, [weekStartsOn])

  const {
    data: { summary = [] },
    loading: queryLoading,
    error,
  } = useAllChannelsPostInsights({
    organizationId: organizationId ?? '',
    startDate,
    endDate,
    comparisonStartDate,
    comparisonEndDate,
  })

  const metrics = useMemo(() => {
    const selected: SummaryEntry[] = []
    for (const type of WEEKLY_PULSE_METRIC_TYPES) {
      const metric = summary.find((entry) => entry.type === type)
      if (metric == null) continue
      selected.push(
        metric.type === 'followers'
          ? { ...metric, description: HOME_FOLLOWERS_DESCRIPTION }
          : metric,
      )
    }
    return selected
  }, [summary])

  const hasData = metrics.length > 0
  // The skeleton reveals after the CSS delay. The hook holds it mounted for a
  // minimum duration, so a response just after the reveal never cuts the fade
  // short. The full condition goes into the hook: the hold must survive the
  // moment data arrives and hasData flips.
  const emptyWhileLoading = queryLoading && !hasData
  const heldLoading = useDelayedLoading(emptyWhileLoading)
  const loading = emptyWhileLoading || heldLoading

  return (
    <WeeklyPulse
      metrics={metrics}
      loading={loading}
      refreshing={queryLoading && hasData}
      error={error}
      comparisonLabel={COMPARISON_LABEL}
      startDate={startDate}
      endDate={endDate}
    />
  )
}
